// batteryApi.ts
// =============
//
// Wave 14ca-fix28 (Battery-Lab Phase 1).
// Frontend-Wrapper für /api/battery/*.
//
// Wave 14ca-fix34: authFetch statt plain fetch, damit iOS-PWAs mit evicted
// Cookie (ITP nach ~7 Tagen Inaktivität) den localStorage-Bearer-Token als
// Backup mitschicken. Sicher seit Backend-Fallback (auth.js) auch bei stale
// Bearer auf den Cookie zurückfällt — keine Regression mehr für Browser, die
// noch ein gültiges Cookie haben.

import { authFetch } from './auth';

export interface BatteryUploadInput {
  gpxXml: string;
  motorId: string;
  mode: string;
  batteryStartPct: number;
  batteryEndPct: number;
  batteryCapacityWh?: number;
  note?: string;
}

export interface BatteryUploadResponse {
  rideId: string;
  ride: {
    distanceKm: number;
    elevationGainM: number;
    elevationLossM: number;
    durationS: number | null;
    consumedWh: number;
  };
  calibration: {
    whPerKm: number;
    whPerMeterUp: number;
    rideCount: number;
  };
}

export interface BatteryPredictInput {
  distanceKm: number;
  elevationGainM: number;
  motorId: string;
  mode: string;
  batteryCapacityWh?: number;
}

export interface BatteryPredictResponse {
  expectedWh: number;
  optimisticWh: number;
  conservativeWh: number;
  expectedPct?: number;
  optimisticPct?: number;
  conservativePct?: number;
  confidence: 'low' | 'medium' | 'high';
  rideCount: number;
  isDefault: boolean;
}

export interface BatteryCalibrationRow {
  motor_id: string;
  mode: string;
  wh_per_km: number;
  wh_per_meter_up: number;
  ride_count: number;
  updated_at: string;
}

/** Wave 14ca-fix38: einzelner gespeicherter Trainings-Ride. Felder spiegeln
    die battery_rides-Tabelle 1:1 (snake_case wie vom Backend). */
export interface BatteryRide {
  id: string;
  motor_id: string;
  mode: string;
  distance_km: number;
  elevation_gain_m: number;
  elevation_loss_m: number | null;
  duration_s: number | null;
  consumed_wh: number;
  battery_start_pct: number;
  battery_end_pct: number;
  battery_capacity_wh: number | null;
  note: string | null;
  created_at: string;
}

export interface BatteryMeResponse {
  calibrations: BatteryCalibrationRow[];
  totalRides: number;
  /** Wave 14ca-fix35: Akku-Kapazitaet aus dem juengsten Ride mit
      gesetztem battery_capacity_wh. Frontend benutzt das im PredictBadge
      statt den 750Wh-Default, damit die %-Anzeige zur echten Bike-
      Kapazitaet passt. null wenn der User noch nie eine Kapazitaet
      eingetragen hat. */
  latestBatteryCapacityWh: number | null;
  /** Wave 14ca-fix38: bis zu 100 juengste Rides des Users (descending nach
      created_at). Wird in der Battery-Lab-Page als Liste angezeigt mit
      Delete-Knopf pro Ride. Optional damit aelterer Backend-Stand (vor
      fix38, kein rides-Feld im Response) nicht den Page-Render crasht. */
  rides?: BatteryRide[];
}

export class BatteryApiError extends Error {
  status: number;
  reason?: string;
  constructor(status: number, message: string, reason?: string) {
    super(message);
    this.name = 'BatteryApiError';
    this.status = status;
    this.reason = reason;
  }
}

async function postJson<T>(url: string, body: unknown): Promise<T> {
  const res = await authFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let reason: string | undefined;
    try {
      const j = (await res.json()) as { reason?: string; error?: string };
      reason = j.reason || j.error;
    } catch {
      /* not json */
    }
    throw new BatteryApiError(res.status, `${url} ${res.status}`, reason);
  }
  return (await res.json()) as T;
}

export function uploadBatteryRide(
  input: BatteryUploadInput,
): Promise<BatteryUploadResponse> {
  return postJson('/api/battery/upload', input);
}

export function predictBattery(
  input: BatteryPredictInput,
): Promise<BatteryPredictResponse> {
  return postJson('/api/battery/predict', input);
}

export async function getBatteryMe(): Promise<BatteryMeResponse> {
  const res = await authFetch('/api/battery/me', { method: 'GET' });
  if (!res.ok) {
    throw new BatteryApiError(res.status, `/api/battery/me ${res.status}`);
  }
  return (await res.json()) as BatteryMeResponse;
}

export interface UpdateBatteryRideInput {
  consumed_wh?: number;
  battery_start_pct?: number;
  battery_end_pct?: number;
  battery_capacity_wh?: number | null;
  note?: string | null;
}

/** Wave 14ca-fix64: Edit eines Rides — nachtraegliches Korrigieren der
 *  Akku-/Verbrauchs-Werte falls der User sich vertippt hat. */
export async function updateBatteryRide(
  id: string,
  input: UpdateBatteryRideInput,
): Promise<{ ride: BatteryRide }> {
  const res = await authFetch(`/api/battery/rides/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    let reason: string | undefined;
    try {
      const body = await res.clone().json();
      reason = body?.error;
    } catch {
      /* ignore */
    }
    throw new BatteryApiError(
      res.status,
      `update ride ${res.status}`,
      reason,
    );
  }
  return res.json();
}

export async function deleteBatteryRide(id: string): Promise<void> {
  const res = await authFetch(`/api/battery/rides/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    throw new BatteryApiError(
      res.status,
      `delete ride ${res.status}`,
    );
  }
}
